import { FC, useMemo, memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Button from 'components/Button'
import UnderlineTabs from 'components/Button/UnderlineTabs'
import { DatePickerInput } from 'components/Input/DatePicker'
import { FlexDiv } from 'components/layout/flex'
import Spacer from 'components/Spacer'
import { USER_INFO_FILTERS_HEIGHT } from 'sections/futures/styles'
import { setUserInfoTableFilter } from 'state/futures/reducer'
import { selectUserInfoTableFilter } from 'state/futures/selectors'
import { TableFilteredFuturesTabs } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import TransactionsDropDown, {
	OrdersHistoryType,
	TransactionsHistoryOptionsType,
	TransactionsHistoryType,
} from './TypeFilterDropDown'

export const TimeFilterValues = ['1d', '1w', '1m', '3m'] as const

export type TimeFilterOptions = (typeof TimeFilterValues)[number]
export type TableFilterType = TransactionsHistoryType | OrdersHistoryType

export type TableFilter = {
	from?: string
	to?: string
	span?: TimeFilterOptions
	type?: TableFilterType
}

type TableFilterProps = {
	tab: TableFilteredFuturesTabs
	showFilterType?: boolean
}

const TableFilters: FC<TableFilterProps> = memo(({ showFilterType = false, ...props }) => {
	const { t } = useTranslation()
	const tableFilter = useAppSelector(selectUserInfoTableFilter(props.tab))
	const dispatch = useAppDispatch()

	const handleDateChange = useCallback(
		(date: Date[]) => {
			dispatch(
				setUserInfoTableFilter({
					tableFilter: {
						to: date[1].toString(),
						from: date[0].toString(),
						type: tableFilter?.type,
					},
					futuresTab: props.tab,
				})
			)
		},
		[dispatch, props.tab, tableFilter]
	)

	const handleResetFilter = useCallback(() => {
		dispatch(
			setUserInfoTableFilter({
				tableFilter: null,
				futuresTab: props.tab,
			})
		)
	}, [dispatch, props.tab])

	const handleTypeFilterChange = useCallback(
		(type: TransactionsHistoryOptionsType) => {
			dispatch(
				setUserInfoTableFilter({
					tableFilter: {
						...tableFilter,
						type: type.value,
					},
					futuresTab: props.tab,
				})
			)
		},
		[dispatch, props.tab, tableFilter]
	)
	return (
		<Container>
			<TimeFilterSpan {...props} />
			<StyledSpacer height={16} width={2} />
			<DatePickerContainer>
				<DatePickerInput
					type="popover"
					label1="Date:"
					prevLabel2="to"
					underline={true}
					date={{ from: tableFilter?.from, to: tableFilter?.to }}
					onChange={handleDateChange}
				/>
			</DatePickerContainer>
			{showFilterType && (
				<>
					<StyledSpacer height={16} width={2} />
					<TransactionsDropDown
						value={tableFilter?.type}
						onChange={handleTypeFilterChange}
						type="transactions"
					/>
				</>
			)}
			<StyledSpacer height={16} width={2} />
			<ResetFilter size="xsmall" onClick={handleResetFilter} disabled={!tableFilter}>
				{t('futures.market.table-filter.reset')}
			</ResetFilter>
		</Container>
	)
})

const TimeFilterSpan: FC<TableFilterProps> = ({ tab }) => {
	const tableFilter = useAppSelector(selectUserInfoTableFilter(tab))
	const dispatch = useAppDispatch()

	const options = useMemo(() => {
		return TimeFilterValues.map((span) => ({
			name: span,
			label: span,
			active: span === tableFilter?.span,
			onClick: () => dispatch(setUserInfoTableFilter({ tableFilter: { span }, futuresTab: tab })),
		}))
	}, [tableFilter, tab, dispatch])

	return (
		<StyledUnderlineTabs options={options} noBorders={true} showIndicator={!!tableFilter?.span} />
	)
}

const Container = styled(FlexDiv)`
	height: ${USER_INFO_FILTERS_HEIGHT}px;
	color: ${(props) => props.theme.colors.selectedTheme.text.label};
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	align-items: center;
	padding: 0 18px;
`

const StyledUnderlineTabs = styled(UnderlineTabs)`
	height: 43px;

	& > div {
		padding-left: 0;
	}
`

const ResetFilter = styled(Button)<{ active?: boolean }>`
	text-transform: uppercase;
	margin: 0 12px;
	border-radius: 100px;
	font-weight: 600;
	height: 24px;
	width: 53px;
	font-size: 10px;
	font-family: ${(props) => props.theme.fonts.regular};
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.calendar.resetButton.color};
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.calendar.resetButton.background};
`

const StyledSpacer = styled(Spacer)`
	border-right: ${(props) => props.theme.colors.selectedTheme.border};
`

const DatePickerContainer = styled(FlexDiv)`
	gap: 10px;
	padding: 0 12px;
`

export default TableFilters
